import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { appToast } from './AppToast'

export default function ReportColumnsModal({
  open = false,
  report = null,
  savedColumns: propSavedColumns,
  onClose,
  onSave,
}) {
  const [draftColumns, setDraftColumns] = useState([])
  const [savedColumns, setSavedColumns] = useState([])
  const [columnSearch, setColumnSearch] = useState('')
  const [draggedColumn, setDraggedColumn] = useState(null)

  useEffect(() => {
    if (!open || !report) return

    const rawCols =
      Array.isArray(propSavedColumns) && propSavedColumns.length > 0
        ? propSavedColumns
        : report.defaultColumns || [
            'Faculty',
            'Date',
            'Time Slot',
            'Course',
            'Subject',
            'Branch',
            'Batch',
            'Classroom',
            'Status',
          ]

    const initialCols = rawCols.map((col) => {
      if (typeof col === 'object') {
        return { key: col.key, label: col.label || col.key, visible: col.visible !== false }
      }
      return { key: col, label: col, visible: true }
    })

    setDraftColumns(initialCols)
    setSavedColumns(initialCols)
    setColumnSearch('')
    setDraggedColumn(null)
  }, [open, report, propSavedColumns])

  if (!open || !report) return null

  const moveDraftColumn = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return
    setDraftColumns((prev) => {
      const sourceIndex = prev.findIndex((item) => item.key === sourceKey)
      const targetIndex = prev.findIndex((item) => item.key === targetKey)
      if (sourceIndex === -1 || targetIndex === -1) return prev
      const next = [...prev]
      const [moved] = next.splice(sourceIndex, 1)
      next.splice(targetIndex, 0, moved)
      return next
    })
  }

  const filteredDraftColumns = draftColumns.filter((col) => {
    if (!columnSearch.trim()) return true
    const q = columnSearch.toLowerCase()
    return col.label.toLowerCase().includes(q) || col.key.toLowerCase().includes(q)
  })

  const handleApply = () => {
    if (onSave) {
      onSave(draftColumns)
    }
    appToast.success(`Column settings saved for ${report.title}`)
    onClose()
  }

  const modalContent = (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 sm:p-6 overflow-hidden">
      <div className="w-full max-w-4xl bg-surface-container-lowest rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row h-[600px] border border-outline-variant/20 animate-in fade-in zoom-in-95 duration-200">
        
        {/* ── Left Column: Configuration List ── */}
        <div className="flex-grow flex flex-col p-6 sm:p-7 bg-white overflow-hidden">
          
          {/* Header */}
          <div className="mb-6 flex justify-between items-start">
            <div>
              <h1 className="font-headline text-2xl font-extrabold tracking-tight text-primary">
                Report Columns
              </h1>
              <p className="text-on-surface-variant text-xs mt-1 max-w-md">
                Customize visible columns and sequence for <span className="font-bold text-on-surface">{report.title}</span>.
              </p>
            </div>
            <button
              type="button"
              className="px-3.5 py-1.5 text-xs font-semibold text-secondary-container bg-surface hover:bg-surface-container-high transition-colors rounded-lg border border-outline-variant/30 cursor-pointer select-none"
              onClick={() => setDraftColumns(savedColumns)}
            >
              Reset to Saved
            </button>
          </div>

          {/* Search bar */}
          <div className="relative mb-4">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-base">
              search
            </span>
            <input
              className="w-full bg-surface-container-low border-none rounded-xl pl-9 pr-4 py-2.5 text-xs focus:ring-2 focus:ring-primary/20 transition-all outline-none"
              placeholder="Search available columns..."
              type="text"
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.target.value)}
            />
          </div>

          {/* Draggable Column List */}
          <div className="flex-grow overflow-y-auto overscroll-contain pr-1 space-y-2.5 custom-scrollbar">
            {filteredDraftColumns.map((column) => (
              <div
                key={column.key}
                draggable
                onDragStart={() => setDraggedColumn(column.key)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault()
                  moveDraftColumn(draggedColumn, column.key)
                  setDraggedColumn(null)
                }}
                className={`group flex items-center gap-3 p-3 rounded-xl transition-colors cursor-default border ${
                  column.visible
                    ? 'bg-surface-container-lowest border-outline-variant/25 hover:bg-surface-container-low shadow-2xs'
                    : 'bg-surface-container-low opacity-60 border-outline-variant/15 hover:bg-surface-container-high'
                }`}
              >
                <span className="material-symbols-outlined text-outline cursor-grab active:cursor-grabbing group-hover:text-primary text-lg select-none">
                  drag_indicator
                </span>
                <div className="flex-grow min-w-0">
                  <p className="font-semibold text-xs text-on-surface truncate">{column.label || column.key}</p>
                  <p className="text-[10px] text-on-surface-variant uppercase tracking-wider opacity-70">
                    {column.key}
                  </p>
                </div>
                <button
                  type="button"
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
                    column.visible ? 'bg-primary' : 'bg-outline-variant'
                  }`}
                  onClick={() =>
                    setDraftColumns((prev) =>
                      prev.map((item) =>
                        item.key === column.key ? { ...item, visible: !item.visible } : item
                      )
                    )
                  }
                  aria-label="Toggle column visibility"
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${
                      column.visible ? 'translate-x-4' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="mt-6 pt-4 border-t border-surface-container flex justify-end gap-3">
            <button
              type="button"
              className="px-5 py-2 text-xs font-semibold text-secondary hover:bg-surface-container-high rounded-xl transition-colors cursor-pointer"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-2 text-xs font-bold text-on-primary bg-primary hover:bg-primary/90 rounded-xl shadow-sm transition active:scale-95 cursor-pointer"
              onClick={handleApply}
            >
              Apply Configuration
            </button>
          </div>
        </div>

        {/* ── Right Column: Customization Tips Sidebar ── */}
        <div className="w-full md:w-72 bg-surface-container-low p-6 flex flex-col border-t md:border-t-0 md:border-l border-outline-variant/20">
          <h3 className="font-headline text-base font-bold text-primary flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-lg">lightbulb</span>
            Customization Tips
          </h3>
          <div className="mt-5 space-y-6 overflow-y-auto overscroll-contain pr-1 text-xs">
            <div>
              <h4 className="font-bold text-[11px] text-on-surface uppercase tracking-wider mb-1.5">
                Reordering
              </h4>
              <p className="text-on-surface-variant leading-relaxed text-xs">
                Click and hold the <span className="font-mono text-primary font-bold">::</span> handle to drag
                columns. Top-most columns appear on the left side of the exported report.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-[11px] text-on-surface uppercase tracking-wider mb-1.5">
                Smart Toggles
              </h4>
              <p className="text-on-surface-variant leading-relaxed text-xs">
                Disabled columns are not deleted; they are simply hidden from the active view. Your preferences
                are saved per report type.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
